import styled from '@emotion/styled';
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { useQueryClient } from 'react-query';

import Table from 'App/components/Table';
import PESService from 'App/services/PESService';
import DeleteModal from 'App/components/DeleteModal';
import ImportantCell from './Cells/ImportantCell';
import ActionGroupCell from './Cells/ActionGroupCell';
import dayjs from 'Shared/dayjs';
import useFetchPatientProfileNotesQuery from 'App/hooks/useFetchPatientProfileNotesQuery';
import { dateOnlySlashesFormat } from 'App/services/DateService';
import { stripAllHtml } from 'App/utils';
import truncateText from 'App/utils/truncateText';

const tableStateReducer = (state, action, prevState) => {
    const initialSort = [{ id: 'important', desc: true }];
    const { type } = action;

    switch (type) {
        case 'resetSortBy': {
            return prevState;
        }
        case 'init': {
            return R.pipe(R.set(R.lensProp('sortBy'), initialSort), R.set(R.lensProp('pageSize'), 5))(state);
        }
        default: {
            return state;
        }
    }
};

const TableStyle = styled(Table)`
    td {
        overflow-wrap: anywhere;
    }
`;

const propTypes = {
    patientId: PropTypes.string.isRequired,
    editNote: PropTypes.func.isRequired,
    showForm: PropTypes.func.isRequired,
    editingNote: PropTypes.shape({
        note: PropTypes.string,
        important: PropTypes.bool,
        uuid: PropTypes.string,
    }),
    isFormOpen: PropTypes.bool,
};

const defaultProps = {
    editingNote: null,
    isFormOpen: false,
};

const PatientNotesTable = ({ patientId, editNote, showForm, editingNote, isFormOpen }) => {
    const { data: notes = [], isFetching: isNotesLoading } = useFetchPatientProfileNotesQuery(patientId);
    const queryClient = useQueryClient();
    const [noteToDetele, setNoteToDelete] = useState({});
    const deleteNote = async () => {
        return PESService.deletePatientProfileNotes(patientId, noteToDetele.uuid).then(() => {
            if (noteToDetele.uuid === editingNote?.uuid) {
                showForm(false);
            }
            queryClient.setQueryData(['patient', patientId, 'notes'], (oldNotes) => {
                const newNotes = [...oldNotes];
                newNotes.splice(noteToDetele.index, 1);
                return newNotes;
            });
            setNoteToDelete({});
        });
    };
    const deleteMessage = ({ style }) => {
        return (
            <>
                <p style={style}>{truncateText(stripAllHtml(noteToDetele.note))}</p>
                <p style={style}>Are you sure you want to delete this note?</p>
            </>
        );
    };
    const columns = useMemo(() => {
        return [
            {
                Header: 'Important',
                accessor: (row) => (row.important ? 1 : 0),
                id: 'important',
                Cell: ({ value, row }) => ImportantCell(value, row.original, patientId, isFormOpen),
                Filter: <span />,
            },
            {
                Header: 'Note',
                accessor: (row) => row.note,
                Cell: ({ value }) => truncateText(stripAllHtml(value)),
                id: 'note',
            },
            {
                Header: 'Updated By',
                accessor: (row) => `${row.updatedByUser.firstName} ${row.updatedByUser.lastName}`,
                id: 'updatedBy',
            },
            {
                Header: 'Updated On',
                accessor: (row) => {
                    return dayjs.utc(row.updatedOn).format(dateOnlySlashesFormat);
                },
                id: 'updatedOn',
            },
            {
                Header: 'Actions',
                id: 'actions',
                Filter: <span />,
                Cell: ({ row }) =>
                    ActionGroupCell(Number(row.index), row.original, editNote, setNoteToDelete, isFormOpen),
            },
        ];
    }, [editNote, patientId, isFormOpen]);
    return (
        <>
            <TableStyle
                getKey={R.prop('uuid')}
                data={notes}
                placeholderText={
                    notes?.length ? 'There are no notes that match the criteria.' : 'No notes have been created.'
                }
                columns={columns}
                isLoading={isNotesLoading}
                pageSizeOptions={[5]}
                stateReducer={tableStateReducer}
            />
            {!!noteToDetele?.uuid && (
                <DeleteModal
                    message={deleteMessage}
                    show
                    onDelete={deleteNote}
                    onHide={() => setNoteToDelete({})}
                    title="Delete Note"
                    confirmText="Delete"
                />
            )}
        </>
    );
};

PatientNotesTable.propTypes = propTypes;
PatientNotesTable.defaultProps = defaultProps;

export default PatientNotesTable;
